import React, { useEffect, useState } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { getOffer, updateOffer, deleteOffer } from "../../services/offers"
import {
  ArrowLeftIcon,
  TagIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  EyeIcon,
  EyeSlashIcon,
  TrashIcon
} from "@heroicons/react/24/outline"

const AdminOfferDetails = () => {
  const { offerId } = useParams()
  const navigate = useNavigate()
  const [offer, setOffer] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [submitting, setSubmitting] = useState(false)

  const fetchOffer = async () => {
    setLoading(true)
    setError("")
    try {
      const res = await getOffer(offerId)
      if (res.status === 200) {
        setOffer(res.data)
      } else {
        setError(res.error || "Offer not found")
      }
    } catch (err) {
      setError("Failed to fetch offer")
    }
    setLoading(false)
  }

  useEffect(() => { fetchOffer() }, [offerId])

  const handleActivate = async () => {
    if (!window.confirm('Are you sure you want to activate this offer? This will make it visible to attendees.')) return

    setSubmitting(true)
    setError("")
    setSuccess("")
    
    try {
      const res = await updateOffer(offerId, { status: 'ACTIVE' })
      if (res.status === 200) {
        setSuccess("Offer activated successfully")
        fetchOffer()
      } else {
        setError(res.error || "Failed to activate offer")
      }
    } catch (err) {
      setError("Failed to activate offer")
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeactivate = async () => {
    if (!window.confirm('Are you sure you want to deactivate this offer? This will hide it from attendees.')) return

    setSubmitting(true)
    setError("")
    setSuccess("")
    
    try {
      const res = await updateOffer(offerId, { status: 'INACTIVE' })
      if (res.status === 200) {
        setSuccess("Offer deactivated successfully")
        fetchOffer()
      } else {
        setError(res.error || "Failed to deactivate offer")
      }
    } catch (err) {
      setError("Failed to deactivate offer")
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm("Are you sure? This will permanently delete the offer and cannot be undone.")) return

    setSubmitting(true)
    setError("")
    setSuccess("")
    
    try {
      const res = await deleteOffer(offerId)
      if (res.status === 200) {
        navigate('/admin/offers')
      } else {
        setError(res.error || "Failed to delete offer")
        setSubmitting(false)
      }
    } catch (err) {
      setError("Failed to delete offer")
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="text-center py-20">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
        <p className="mt-2 text-slate-500">Loading offer...</p>
      </div>
    )
  }

  if (error || !offer) {
    return (
      <div className="max-w-3xl mx-auto">
        <Link to="/admin/offers" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-6">
          <ArrowLeftIcon className="w-4 h-4" /> Back to Offers
        </Link>
        <div className="p-4 rounded-xl bg-red-50 text-red-600 border border-red-100 text-center">
          {error || "Offer not found"}
        </div>
      </div>
    )
  }

  const discountPercentage = offer.normalPrice
    ? Math.round(((offer.normalPrice - offer.discountedPrice) / offer.normalPrice) * 100)
    : 0

  return (
    <div className="max-w-4xl mx-auto">
      <Link to="/admin/offers" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-6">
        <ArrowLeftIcon className="w-4 h-4" /> Back to Offers
      </Link>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Header */}
        <div className="p-8 border-b border-slate-100">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-cyan-100 flex items-center justify-center">
                <TagIcon className="w-8 h-8 text-cyan-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900">{offer.title}</h1>
                <p className="text-slate-500 mt-1">Offer ID: {offer._id}</p>
              </div>
            </div>
            <span className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-bold uppercase ${
              offer.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
              offer.status === 'DRAFT' ? 'bg-slate-100 text-slate-700' :
              offer.status === 'EXPIRED' ? 'bg-red-100 text-red-700' :
              'bg-slate-100 text-slate-700'
            }`}>
              {offer.status}
            </span>
          </div>
        </div>

        {/* Images */}
        {offer.images && offer.images.length > 0 && (
          <div className="p-8 border-b border-slate-100">
            <h3 className="text-sm font-bold text-slate-400 uppercase mb-4">Images</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {offer.images.map((img, i) => (
                <img key={i} src={img} alt="" className="w-full h-32 object-cover rounded-lg" />
              ))}
            </div>
          </div>
        )}

        {/* Details Grid */}
        <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-8 border-b border-slate-100">
          <div>
            <h3 className="text-sm font-bold text-slate-400 uppercase mb-4">Center Information</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <BuildingOfficeIcon className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Organization</p>
                  <p className="font-semibold text-slate-900">{offer.centerId?.organization || 'Unknown'}</p>
                </div>
              </div>
              {offer.centerId?.email && (
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="text-slate-900">{offer.centerId.email}</p>
                </div>
              )}
              {offer.centerId?.phoneNumber && (
                <div>
                  <p className="text-sm text-slate-500">Phone</p>
                  <p className="text-slate-900">{offer.centerId.phoneNumber}</p>
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold text-slate-400 uppercase mb-4">Pricing & Availability</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <CurrencyDollarIcon className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Discounted Price</p>
                  <p className="text-2xl font-bold text-green-600">₦{offer.discountedPrice?.toLocaleString()}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-slate-500">Normal Price</p>
                <p className="text-lg font-semibold text-slate-400 line-through">₦{offer.normalPrice?.toLocaleString()}</p>
                <p className="text-sm text-green-600 font-medium">Save {discountPercentage}%</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Availability</p>
                <p className="font-semibold text-slate-900">{offer.remaining} / {offer.quantity} remaining</p>
              </div>
            </div>
          </div>
        </div>

        {/* Validity Period */}
        {(offer.validFrom || offer.validUntil) && (
          <div className="p-8 border-b border-slate-100">
            <h3 className="text-sm font-bold text-slate-400 uppercase mb-4">Validity Period</h3>
            <div className="flex items-center gap-6">
              {offer.validFrom && (
                <div className="flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Valid From</p>
                    <p className="font-semibold text-slate-900">{new Date(offer.validFrom).toLocaleDateString()}</p>
                  </div>
                </div>
              )}
              {offer.validUntil && (
                <div className="flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Valid Until</p>
                    <p className="font-semibold text-slate-900">{new Date(offer.validUntil).toLocaleDateString()}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Description */}
        {offer.description && (
          <div className="p-8 border-b border-slate-100">
            <h3 className="text-sm font-bold text-slate-400 uppercase mb-4">Description</h3>
            <p className="text-slate-700 whitespace-pre-wrap">{offer.description}</p>
          </div>
        )}

        {/* Terms */}
        {offer.terms && (
          <div className="p-8">
            <h3 className="text-sm font-bold text-slate-400 uppercase mb-4">Terms & Conditions</h3>
            <p className="text-slate-700 whitespace-pre-wrap">{offer.terms}</p>
          </div>
        )}

        {/* Metadata */}
        <div className="p-8 bg-slate-50 border-t border-slate-100">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Created</p>
              <p className="font-semibold text-slate-900">{new Date(offer.createdAt).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-slate-500">Updated</p>
              <p className="font-semibold text-slate-900">{new Date(offer.updatedAt).toLocaleString()}</p>
            </div>
            {offer.category && (
              <div>
                <p className="text-slate-500">Category</p>
                <p className="font-semibold text-slate-900">{offer.category}</p>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="p-8 border-t border-slate-100 bg-white">
          {error && (
            <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-xl border border-red-100">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-4 bg-green-50 text-green-600 rounded-xl border border-green-100">
              {success}
            </div>
          )}
          <div className="flex flex-wrap gap-4">
            {offer.status === 'ACTIVE' ? (
              <button
                onClick={handleDeactivate}
                disabled={submitting}
                className="flex items-center gap-2 px-4 py-2 border border-orange-200 bg-orange-50 text-orange-700 rounded-lg font-medium hover:bg-orange-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <EyeSlashIcon className="w-4 h-4" />
                Deactivate Offer
              </button>
            ) : (
              <button
                onClick={handleActivate}
                disabled={submitting}
                className="flex items-center gap-2 px-4 py-2 border border-green-200 bg-green-50 text-green-700 rounded-lg font-medium hover:bg-green-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <EyeIcon className="w-4 h-4" />
                Activate Offer
              </button>
            )}
            <button
              onClick={handleDelete}
              disabled={submitting}
              className="ml-auto flex items-center gap-2 px-4 py-2 border border-red-200 text-red-600 rounded-lg font-medium hover:bg-red-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <TrashIcon className="w-4 h-4" />
              Delete Offer
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminOfferDetails

